import { useState } from "react";
import SEO from "components/SEO/SEO";
import {
    CalendarIcon,
    UserGroupIcon,
    ShieldCheckIcon,
    CreditCardIcon,
    PhoneIcon,
    GlobeAltIcon,
    CheckCircleIcon,
    SparklesIcon,
} from "@heroicons/react/24/outline";
import image from "../../images/cars/9.png";
import image2 from "../../images/logo2.jpg";

export default function AboutUs() {
    const [activeTab, setActiveTab] = useState("story");

    const testimonials = [
        {
            name: "Sarah Johnson",
            role: "Business Traveler",
            content:
                "NomadRoof made finding accommodation for my extended business trips so much easier. The verified properties gave me peace of mind.",
            image: { image },
        },
        {
            name: "Miguel Fernandez",
            role: "Exchange Student",
            content:
                "As an exchange student, I was worried about finding a place to live. NomadRoof's transparent booking process made everything simple.",
            image: { image },
        },
        {
            name: "Elena Petrova",
            role: "Property Owner",
            content:
                "Since listing my properties on NomadRoof, I've had consistent bookings with reliable tenants. The platform handles everything.",
            image: { image },
        },
    ];

    const benefits = [
        {
            icon: ShieldCheckIcon,
            title: "Verified Properties",
            description: "Every property is personally verified by our team to ensure what you see is what you get.",
        },
        {
            icon: CreditCardIcon,
            title: "Secure Payments",
            description: "Our secure payment system protects both guests and property owners with transparent transactions.",
        },
        {
            icon: PhoneIcon,
            title: "24/7 Support",
            description: "Our dedicated support team is always available to help with any questions or issues that arise.",
        },
        {
            icon: CalendarIcon,
            title: "Flexible Stays",
            description: "From one month to one year, find the perfect accommodation for your needs.",
        },
        {
            icon: UserGroupIcon,
            title: "Vetted Guests",
            description: "Property owners can rest easy knowing all guests are verified and reviewed.",
        },
        {
            icon: GlobeAltIcon,
            title: "Global Reach",
            description: "Find accommodations in major cities worldwide, with local support in each location.",
        },
    ];

    return (
        <div className="font-sans text-gray-800 dark:text-gray-100">
            <SEO
              title="About Nomadroof — Student Housing Platform in Lima"
              description="Learn about Nomadroof, the student accommodation platform connecting students and Erasmus with verified properties in Lima, Peru."
              canonical="/about-nomadroof"
            />
            {/* Hero Section - Compact */}
            <section className="relative py-12 sm:py-16 md:py-20 overflow-hidden bg-gradient-to-br from-[#f5694b] via-[#f5694b] to-[#d94a31]">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-20"
                    style={{ backgroundImage: `url(${image2})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                
                {/* Decorative elements */}
                <div className="absolute top-10 left-5 sm:left-10 w-40 sm:w-56 h-40 sm:h-56 bg-white/10 rounded-full blur-3xl" />
                <div className="absolute bottom-10 right-5 sm:right-10 w-48 sm:w-64 h-48 sm:h-64 bg-[#f5694b]/20 rounded-full blur-3xl" />
                
                <div className="container mx-auto px-4 sm:px-6 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4 border border-white/30">
                            <SparklesIcon className="w-4 h-4 text-white" />
                            <span className="text-xs font-medium text-white">Your Trusted Accommodation Partner</span>
                        </div>
                        
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 drop-shadow-lg leading-tight">
                            Welcome to <span className="text-white/90">NomadRoof</span>
                        </h1>
                        
                        <p className="text-sm sm:text-base md:text-lg text-white/90 mb-6 sm:mb-8 drop-shadow-md max-w-2xl mx-auto">
                            Your trusted partner for mid to long-term accommodations worldwide
                        </p>
                        
                        <div className="flex flex-col sm:flex-row justify-center gap-3 px-4">
                            <button className="w-full sm:w-auto bg-white text-[#f5694b] px-6 py-2.5 rounded-full font-semibold text-sm shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
                                Explore Properties
                            </button>
                            <button className="w-full sm:w-auto bg-transparent border-2 border-white text-white px-6 py-2.5 rounded-full font-semibold text-sm hover:bg-white hover:text-[#f5694b] transition-all duration-300">
                                List Your Property
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section - Compact */}
            <section className="py-6 sm:py-8 bg-white dark:bg-gray-900 border-y border-gray-200 dark:border-gray-800">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                        {[
                            { number: "10K+", label: "Happy Guests" },
                            { number: "2K+", label: "Properties" },
                            { number: "50+", label: "Cities" },
                            { number: "4.9", label: "Average Rating" },
                        ].map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-2xl sm:text-3xl font-bold text-[#f5694b] mb-1">
                                    {stat.number}
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">
                                    {stat.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tabbed Content Section - Compact */}
            <section className="container mx-auto px-4 sm:px-6 py-10 sm:py-14 md:py-16">
                {/* Tab Navigation */}
                <div className="flex justify-center mb-8 sm:mb-10">
                    <div className="inline-flex flex-col sm:flex-row gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-1.5 shadow-md w-full sm:w-auto max-w-md sm:max-w-none">
                        {[
                            { label: "Our Story", key: "story" },
                            { label: "For Travelers", key: "travelers" },
                            { label: "For Owners", key: "owners" },
                        ].map(({ label, key }) => (
                            <button
                                key={key}
                                onClick={() => setActiveTab(key)}
                                className={`w-full sm:w-auto px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-300 focus:outline-none
                                    ${activeTab === key
                                        ? "bg-[#f5694b] text-white shadow-md"
                                        : "text-gray-700 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-700"
                                    }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="grid md:grid-cols-2 gap-8 md:gap-10 lg:gap-12 items-center max-w-6xl mx-auto">
                    {/* Text Content */}
                    <div className="order-2 md:order-1 space-y-4">
                        {activeTab === "story" && (
                            <div className="animate-fadeIn">
                                <div className="inline-block bg-[#f5694b]/10 dark:bg-[#f5694b]/20 text-[#f5694b] px-3 py-1 rounded-full text-xs font-semibold mb-3">
                                    Our Journey
                                </div>
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                                    Born from the Spirit of Adventure
                                </h2>
                                <div className="space-y-3 text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
                                    <p>
                                        The adventure calls us—we as nomads—to move from one place
                                        to another, to explore new cities, learn other languages,
                                        meet new people, and experience different cultures. However,
                                        we also understand how important it is to find a place
                                        abroad to live in and feel secure.
                                    </p>
                                    <p>
                                        That is why we created <span className="font-semibold text-[#f5694b]">NomadRoof</span>, an online platform for mid
                                        to long-term accommodation needs that helps people move to a
                                        new home and property owners who want to rent out their
                                        spaces.
                                    </p>
                                </div>
                                
                                <div className="mt-4 space-y-2">
                                    {[
                                        "Built by travelers, for travelers",
                                        "Focused on long-term stays",
                                        "Global reach, local expertise"
                                    ].map((point, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#f5694b] flex-shrink-0" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">{point}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab === "travelers" && (
                            <div className="animate-fadeIn">
                                <div className="inline-block bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                                    For Travelers & Guests
                                </div>
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                                    Your Home Away from Home
                                </h2>
                                <div className="space-y-3 text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
                                    <p>
                                        We offer travelers, exchange students, professionals, and
                                        expats a curated selection of attractive properties with a
                                        simple booking interface—no hassle of in-person viewings
                                        required.
                                    </p>
                                    <p>
                                        You don't have to worry about scams or no-show viewings as
                                        we personally verify the hosts and their properties, taking
                                        the pictures you see in the listings. Read reviews from past
                                        guests to make an informed decision and enjoy our premium
                                        customer service throughout your stay.
                                    </p>
                                </div>
                                
                                <div className="mt-4 space-y-2">
                                    {[
                                        "No in-person viewings needed",
                                        "Verified hosts & properties",
                                        "Premium customer support"
                                    ].map((point, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">{point}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab === "owners" && (
                            <div className="animate-fadeIn">
                                <div className="inline-block bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                                    For Property Owners
                                </div>
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                                    Effortless Property Management
                                </h2>
                                <div className="space-y-3 text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
                                    <p>
                                        We offer property owners the convenience of managing all
                                        their properties online in a professional way. If you don't
                                        have the right pictures, we'll take care of a photoshoot and
                                        create an appealing profile.
                                    </p>
                                    <p>
                                        No more calls, messages, emails, and missed viewings. Forget
                                        about scheduling in-person tours and reading thousands of
                                        inquiries—get your property booked easily with our
                                        streamlined platform and connect with reliable, verified
                                        guests.
                                    </p>
                                </div>
                                
                                <div className="mt-4 space-y-2">
                                    {[
                                        "Professional photography included",
                                        "No more scheduling headaches",
                                        "Verified, reliable guests"
                                    ].map((point, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">{point}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Image */}
                    <div className="order-1 md:order-2">
                        <div className="relative">
                            <div className="absolute -inset-3 bg-gradient-to-r from-[#f5694b] to-[#f5694b]/70 rounded-2xl blur-xl opacity-20"></div>
                            
                            <div className="relative rounded-2xl overflow-hidden shadow-xl">
                                <img
                                    src={image2}
                                    alt="NomadRoof accommodations"
                                    className="w-full h-[250px] sm:h-[300px] md:h-[350px] object-cover transform hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                            </div>
                            
                            <div className="absolute -bottom-3 -right-3 bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                                <div className="text-center">
                                    <div className="text-xl sm:text-2xl font-bold text-[#f5694b]">4.9</div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">★★★★★</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">Rating</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Section - Compact */}
            <section className="py-10 sm:py-14 md:py-16 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="text-center mb-8 sm:mb-10">
                        <div className="inline-block bg-[#f5694b]/10 dark:bg-[#f5694b]/20 text-[#f5694b] px-3 py-1 rounded-full text-xs font-semibold mb-2">
                            Why Choose Us
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                            Everything You Need, All in One Place
                        </h2>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                            We've built a platform that takes care of every detail
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto">
                        {benefits.map((benefit, index) => {
                            const Icon = benefit.icon;
                            return (
                                <div
                                    key={index}
                                    className="group relative bg-white dark:bg-gray-800 rounded-xl p-5 sm:p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700 hover:border-[#f5694b] dark:hover:border-[#f5694b] hover:-translate-y-1"
                                >
                                    <div className="relative inline-flex mb-3 sm:mb-4">
                                        <div className="absolute inset-0 bg-[#f5694b] rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity"></div>
                                        <div className="relative bg-[#f5694b] p-2.5 sm:p-3 rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300">
                                            <Icon className="text-white w-5 h-5 sm:w-6 sm:h-6" />
                                        </div>
                                    </div>
                                    
                                    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2">
                                        {benefit.title}
                                    </h3>
                                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                        {benefit.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Testimonials Section - Compact */}
            <section className="py-10 sm:py-14 md:py-16 bg-white dark:bg-gray-900">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="text-center mb-8 sm:mb-10">
                        <div className="inline-block bg-[#f5694b]/10 dark:bg-[#f5694b]/20 text-[#f5694b] px-3 py-1 rounded-full text-xs font-semibold mb-2">
                            Testimonials
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                            Loved by Thousands Worldwide
                        </h2>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                            Don't just take our word for it—hear from our community
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto">
                        {testimonials.map((testimonial, index) => (
                            <div
                                key={index}
                                className="group relative bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 rounded-xl p-5 sm:p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700 hover:-translate-y-1"
                            >
                                <div className="absolute top-3 sm:top-4 right-3 sm:right-4 text-3xl sm:text-4xl text-[#f5694b]/10 font-serif">"</div>
                                
                                <div className="relative">
                                    <div className="flex gap-0.5 mb-3 sm:mb-4">
                                        {[...Array(5)].map((_, i) => (
                                            <svg key={i} className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#f5694b] fill-current" viewBox="0 0 20 20">
                                                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                            </svg>
                                        ))}
                                    </div>
                                    
                                    <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base italic mb-4 sm:mb-5 leading-relaxed">
                                        "{testimonial.content}"
                                    </p>
                                    
                                    <div className="flex items-center gap-3">
                                        <img
                                            src="https://images.pexels.com/photos/3250613/pexels-photo-3250613.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"
                                            alt={testimonial.name}
                                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover ring-3 ring-[#f5694b]/20"
                                        />
                                        <div>
                                            <h4 className="font-bold text-gray-900 dark:text-white text-sm sm:text-base">
                                                {testimonial.name}
                                            </h4>
                                            <p className="text-gray-500 dark:text-gray-400 text-xs">
                                                {testimonial.role}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section - Compact */}
            <section className="relative py-12 sm:py-14 md:py-16 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#f5694b] via-[#f5694b] to-[#d94a31]"></div>
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00ek0yMCAyMGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
                
                <div className="absolute top-5 left-5 w-40 sm:w-48 h-40 sm:h-48 bg-white/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-5 right-5 w-48 sm:w-56 h-48 sm:h-56 bg-[#f5694b]/20 rounded-full blur-3xl"></div>
                
                <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 leading-tight">
                        Ready to Find Your Perfect Stay?
                    </h2>
                    <p className="text-sm sm:text-base md:text-lg text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto">
                        Whether you're traveling for work, study, or adventure, NomadRoof makes finding your next home simple and secure.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row justify-center gap-3 px-4">
                        <button className="group w-full sm:w-auto bg-white text-[#f5694b] px-7 py-2.5 rounded-full font-semibold text-sm shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2">
                            Get Started Now
                            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </button>
                        <button className="w-full sm:w-auto bg-transparent border-2 border-white text-white px-7 py-2.5 rounded-full font-semibold text-sm hover:bg-white hover:text-[#f5694b] transition-all duration-300 backdrop-blur-sm">
                            Contact Us
                        </button>
                    </div>
                    
                    <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-5 text-white/80 text-xs sm:text-sm">
                        <div className="flex items-center gap-1.5">
                            <CheckCircleIcon className="w-4 h-4" />
                            <span>No Hidden Fees</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <CheckCircleIcon className="w-4 h-4" />
                            <span>Instant Booking</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <CheckCircleIcon className="w-4 h-4" />
                            <span>24/7 Support</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}